import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Link2, MessageSquare, Unlink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Initials } from "@/components/ui/list-row";
import { StatusBadge } from "@/components/ui/status-badge";
import PageHeader from "@/components/common/PageHeader";
import EmptyStateSection from "@/components/common/EmptyStateSection";
import Filo from "@/components/messaggi/Filo";
import { NuovoClienteDialog } from "@/pages/ClientiPage";
import { erroreDi, giorno, giornoDi, oggi, ora } from "@/lib/crm";
import { statoModelli, testoDelModello } from "@/lib/modelli";
import { cn } from "@/lib/utils";

function useAdesso(ogniMs = 30000) {
  const [adesso, setAdesso] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setAdesso(Date.now()), ogniMs);
    return () => clearInterval(t);
  }, [ogniMs]);
  return adesso;
}

function quandoBreve(iso) {
  if (!iso) return "";
  return giornoDi(iso) === oggi() ? ora(iso) : giorno(iso);
}

/* I filtri guardano le conversazioni ancora vive: una chiusa non aspetta nessuno. «Da rispondere»: l'ultimo messaggio
   è del cliente. «Senza nessuno»: nessuno la segue. «Le mie»: la segue chi guarda. Al venditore le conversazioni
   arrivano già affidate a lui: «Senza nessuno» e «Le mie» non gli direbbero niente di più. */
const FILTRI = {
  tutte: { label: "Tutte", vale: () => true, contare: false },
  da_rispondere: { label: "Da rispondere", vale: (c) => c.stato !== "chiusa" && c.ultimo?.direction === "in" },
  senza_nessuno: { label: "Senza nessuno", vale: (c) => c.stato !== "chiusa" && !c.assigned_to, soloTitolare: true },
  mie: { label: "Le mie", vale: (c, io) => Boolean(io) && c.assigned_to === io, soloTitolare: true },
};

/* ── L'elenco ──────────────────────────────────────────────────────────────── */
function Elenco({ conversazioni, scelta, onScegli }) {
  return (
    <ul className="grid" aria-label="Conversazioni">
      {conversazioni.map((c) => {
        const attiva = c.wa_id === scelta;
        const anteprima = c.ultimo
          ? `${c.ultimo.direction === "out" ? "Tu: " : ""}${testoDelModello(c.ultimo.testo) || c.ultimo.testo || (c.ultimo.tipo === "image" ? "Foto" : "Allegato")}`
          : "Nessun messaggio";
        return (
          <li key={c.wa_id}>
            <button
              type="button"
              onClick={() => onScegli(c.wa_id)}
              aria-current={attiva ? "true" : undefined}
              data-testid={`conversazione-${c.wa_id}`}
              className={cn(
                "flex min-h-16 w-full items-start gap-group border-b border-line-subtle px-group py-group text-left transition-colors duration-150 ease-enter",
                "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
                attiva ? "bg-surface-2 shadow-[inset_0_0_0_2px_var(--brand)]" : "hover:bg-surface-2",
                c.stato === "chiusa" && !attiva && "opacity-70",
              )}
            >
              <Initials name={c.nome} />
              <span className="grid min-w-0 flex-1 grid-cols-1 gap-micro">
                <span className="flex items-baseline justify-between gap-inside">
                  <span className="truncate font-bold text-ink-strong">{c.nome}</span>
                  <span className="shrink-0 text-caption tabular-nums text-ink-2">{quandoBreve(c.attivita_at)}</span>
                </span>
                <span className="truncate text-small text-ink-2">{anteprima}</span>
                <span className="mt-micro flex flex-wrap items-center gap-inside text-caption text-ink-2">
                  {c.escalation ? <StatusBadge tone="warning">Da richiamare</StatusBadge> : null}
                  {c.suggerimenti ? <StatusBadge tone="brand">{c.suggerimenti === 1 ? "1 proposta" : `${c.suggerimenti} proposte`}</StatusBadge> : null}
                  <span>{c.segue ? `Segue ${c.segue}` : "Senza nessuno"}</span>
                  {!c.finestra.aperta ? <span>· finestra chiusa</span> : null}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function MessaggiPage() {
  const { profile } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const scelta = params.get("contatto");
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [filtro, setFiltro] = useState("tutte");
  const [modelli, setModelli] = useState(null); // null = non so ancora
  const [nuovoDa, setNuovoDa] = useState(null); // il filo da cui si aggiunge un cliente
  const [versione, setVersione] = useState(0);
  const adesso = useAdesso();

  const carica = useCallback(() => {
    api
      .get("/messaggi")
      .then((r) => {
        setDati(r.data.conversazioni);
        setErrore(null);
      })
      .catch((e) => setErrore(erroreDi(e)));
  }, []);

  useEffect(() => {
    carica();
    const t = setInterval(carica, 15000);
    return () => clearInterval(t);
  }, [carica]);

  const leggiModelli = useCallback(() => statoModelli().then(setModelli), []);
  useEffect(() => {
    leggiModelli();
  }, [leggiModelli]);

  const scegli = (waId) => setParams(waId ? { contatto: waId } : {}, { replace: false });
  const indietro = useCallback(() => setParams({}, { replace: false }), [setParams]);

  const titolare = profile?.ruolo === "titolare";
  const filtri = useMemo(
    () =>
      Object.entries(FILTRI)
        .filter(([, f]) => titolare || !f.soloTitolare)
        .map(([value, f]) => ({ value, label: f.contare === false ? f.label : `${f.label} ${(dati || []).filter((c) => f.vale(c, profile?.id)).length}` })),
    [dati, profile?.id, titolare],
  );
  const visibili = useMemo(() => (dati || []).filter((c) => FILTRI[filtro].vale(c, profile?.id)), [dati, filtro, profile?.id]);

  /* «Aggiungi come cliente»: «Nuovo cliente» con nome e numero già scritti e la fonte WhatsApp; salvato, il
     contatto si aggancia al cliente come con «Aggancia». */
  const aggancia = async (cliente) => {
    try {
      await api.patch(`/messaggi/${nuovoDa.contatto.wa_id}`, { cliente_id: cliente.id });
      toast.success("Cliente aggiunto e collegato alla conversazione");
      setVersione((v) => v + 1);
      carica();
    } catch (err) {
      toast.error(`Cliente aggiunto, ma la conversazione non si è collegata: usa «Aggancia a un cliente». ${erroreDi(err)}`);
    }
  };

  return (
    <section data-testid="page-messaggi" className="space-y-card">
      {/* Sul telefono, a conversazione aperta, la testata lascia il posto al filo:
          il ritorno all'elenco sta nella testata del filo. */}
      <div className={cn(scelta && "hidden lg:block")}>
        <PageHeader
          eyebrow="WhatsApp"
          title="Messaggi"
          description="Il WhatsApp dello showroom. L'assistente propone, chi segue la conversazione decide."
          actions={
            dati?.length ? (
              <Segmented value={filtro} onValueChange={setFiltro} options={filtri} aria-label="Quali conversazioni" className="max-md:grid max-md:w-full max-md:grid-cols-2" />
            ) : null
          }
        />
      </div>
      {modelli?.collegato === false ? (
        <div role="alert" className="flex items-start gap-group rounded-md border border-danger bg-danger-bg p-group text-body text-danger-ink" data-testid="messaggi-scollegato">
          <Unlink className="mt-micro size-5 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-bold">Il WhatsApp dello showroom si è scollegato</p>
            <p>I messaggi che mandi da qui e quelli automatici non partono. Di solito succede quando scade l'accesso a Meta: lo ricollega Strake. Nel frattempo i clienti si sentono dal telefono.</p>
            <p className="mt-micro text-small">Codice per l'assistenza: WA-190</p>
          </div>
          <Button variant="secondary" size="sm" className="shrink-0" onClick={leggiModelli}>
            Riprova
          </Button>
        </div>
      ) : null}
      {errore ? (
        <div role="alert" className="rounded-md border border-danger bg-danger-bg px-card py-group text-body text-danger-ink">
          {errore}
        </div>
      ) : null}
      {dati === null && !errore ? (
        <div className="grid gap-group rounded-lg border border-line bg-surface p-group" aria-busy="true" aria-label="Caricamento conversazioni" data-testid="messaggi-caricamento">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-group">
              <Skeleton className="size-10 shrink-0 rounded-full" />
              <div className="grid flex-1 gap-inside">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : dati && dati.length === 0 ? (
        <EmptyStateSection
          icon={MessageSquare}
          title="Nessuna conversazione"
          description="Quando qualcuno scrive al numero dello showroom, la conversazione compare qui. Se sei un venditore, vedi quelle che il titolare ti affida."
          testId="messaggi-vuoto"
        />
      ) : dati ? (
        <div
          className={cn(
            "grid grid-cols-1 overflow-hidden rounded-lg border border-line bg-surface lg:h-[calc(100dvh-15rem)] lg:min-h-[32rem] lg:grid-cols-[20rem_minmax(0,1fr)]",
            /* Sul telefono il filo prende l'altezza dello schermo meno la testata e la
               barra in basso, così il campo per scrivere resta sempre sotto il pollice. */
            scelta && "h-[calc(100dvh-11rem)]",
          )}
        >
          <div className={cn("min-h-0 overflow-y-auto lg:border-r lg:border-line-subtle", scelta && "hidden lg:block")} data-testid="messaggi-elenco">
            {visibili.length ? (
              <Elenco conversazioni={visibili} scelta={scelta} onScegli={scegli} />
            ) : (
              <div className="grid justify-items-start gap-group p-card" data-testid="messaggi-filtro-vuoto">
                <p className="text-body text-ink-2">Nessuna conversazione con questo filtro.</p>
                <Button variant="secondary" size="sm" onClick={() => setFiltro("tutte")}>
                  Mostra tutte
                </Button>
              </div>
            )}
          </div>
          <div className={cn("min-h-0 min-w-0", !scelta && "hidden lg:block")}>
            {scelta ? (
              <Filo
                key={scelta}
                waId={scelta}
                onIndietro={indietro}
                onCambiato={carica}
                adesso={adesso}
                modelloPronto={Boolean(modelli?.modello?.pronto)}
                versione={versione}
                onAggiungi={(f) => setNuovoDa(f)}
              />
            ) : (
              <div className="grid h-full place-items-center p-card text-center">
                <div className="grid justify-items-center gap-inside">
                  <Link2 className="size-5 text-ink-3" aria-hidden />
                  <p className="text-body text-ink-2">Scegli una conversazione a sinistra.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <NuovoClienteDialog
        open={Boolean(nuovoDa)}
        onOpenChange={(v) => !v && setNuovoDa(null)}
        onCreato={() => {}}
        iniziale={nuovoDa ? { nome: nuovoDa.contatto.profile_name || "", telefono: `+${nuovoDa.contatto.wa_id}`, fonti: ["whatsapp"] } : null}
        dopoCreazione={aggancia}
      />
    </section>
  );
}
